import {
  createDefaultProgressState,
  type ProgressState,
} from "../application/progress-state";
import {
  gameplayConfig,
  type SkinId,
  type UpgradeId,
} from "../config/gameplay-config";
import { progressConfig } from "../config/progress-config";
import {
  isSelectedSoundtrackId,
  type SelectedSoundtrackId,
} from "../config/soundtrack-config";
import {
  isArenaBackgroundId,
  type ArenaBackgroundId,
} from "../config/arena-background-config";
import {
  isRoundSettlementReceipt,
  type RoundSettlementReceipt,
} from "../application/round-settlement";
import {
  isShareableChallengeIdentity,
  type ShareableChallengeIdentity,
} from "../config/challenge-link-config";
import type { ChallengeRecord } from "../application/progress-state";

export interface ProgressDecodeResult {
  readonly needsRepair: boolean;
  readonly state: ProgressState;
  readonly writeLocked: boolean;
}

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;

const sanitizeCounter = (value: unknown, fallback: number): number => {
  if (typeof value !== "number" || !Number.isInteger(value)) {
    return fallback;
  }
  return Math.min(Number.MAX_SAFE_INTEGER, Math.max(0, value));
};

const sanitizeLevel = (value: unknown, fallback: number): number =>
  Math.min(
    gameplayConfig.growth.maximumLevel,
    sanitizeCounter(value, fallback),
  );

const sanitizeBoolean = (value: unknown, fallback: boolean): boolean =>
  typeof value === "boolean" ? value : fallback;

const configuredSkinIds = new Set<string>(progressConfig.skinIds);

const sanitizeUnlockedSkinIds = (value: unknown): readonly SkinId[] => {
  const result: SkinId[] = [progressConfig.defaultSkinId];
  if (!Array.isArray(value)) {
    return result;
  }
  const seen = new Set<string>(result);
  for (const candidate of value) {
    if (
      typeof candidate === "string" &&
      configuredSkinIds.has(candidate) &&
      !seen.has(candidate)
    ) {
      seen.add(candidate);
      result.push(candidate as SkinId);
    }
  }
  return result;
};

const sanitizeUpgrade = (value: unknown, id: UpgradeId): number =>
  Math.min(progressConfig.upgradeMaxLevels[id], sanitizeCounter(value, 0));

const sanitizeSettlementReceipts = (
  value: unknown,
  excludedIdentifiers: ReadonlySet<string> = new Set(),
  maximum = Number.MAX_SAFE_INTEGER,
): readonly RoundSettlementReceipt[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  const receipts: RoundSettlementReceipt[] = [];
  const identifiers = new Set<string>();
  for (const candidate of value) {
    if (
      isRoundSettlementReceipt(candidate) &&
      !excludedIdentifiers.has(candidate.id) &&
      !identifiers.has(candidate.id)
    ) {
      identifiers.add(candidate.id);
      receipts.push({ ...candidate });
    }
  }
  return receipts.slice(-maximum);
};

const sanitizeDailyChallengeRecords = (
  value: unknown,
): readonly ChallengeRecord[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  const records = new Map<ShareableChallengeIdentity, ChallengeRecord>();
  for (const candidate of value) {
    const record = asRecord(candidate);
    if (
      record?.completed !== true ||
      !isShareableChallengeIdentity(record.identity) ||
      typeof record.bestLevel !== "number" ||
      !Number.isSafeInteger(record.bestLevel) ||
      record.bestLevel < 1 ||
      record.bestLevel > gameplayConfig.growth.maximumLevel
    ) {
      continue;
    }
    const existing = records.get(record.identity);
    if (existing === undefined) {
      records.set(record.identity, {
        bestLevel: record.bestLevel,
        completed: true,
        identity: record.identity,
      });
      continue;
    }
    if (record.bestLevel > existing.bestLevel) {
      records.set(record.identity, {
        ...existing,
        bestLevel: record.bestLevel,
      });
    }
  }
  return [...records.values()].slice(
    -progressConfig.maximumDailyChallengeRecords,
  );
};

const migrateStandardPersonalBest = (
  receipts: readonly RoundSettlementReceipt[],
): number =>
  receipts
    .filter((receipt) => receipt.mode === "standard")
    .reduce((best, receipt) => Math.max(best, receipt.finalLevel), 0);

const sanitizePersonalBestLevels = (
  record: Record<string, unknown> | undefined,
  receipts: readonly RoundSettlementReceipt[],
): ProgressState["personalBestLevels"] => {
  if (record?.schemaVersion === 1) {
    return {
      "challenge-1": 0,
      "challenge-5": 0,
      standard: migrateStandardPersonalBest(receipts),
    };
  }
  const personalBestLevels = asRecord(record?.personalBestLevels);
  return {
    "challenge-1": sanitizeLevel(personalBestLevels?.["challenge-1"], 0),
    "challenge-5": sanitizeLevel(personalBestLevels?.["challenge-5"], 0),
    standard: sanitizeLevel(personalBestLevels?.standard, 0),
  };
};

const sanitizeProgressRecord = (
  record: Record<string, unknown> | undefined,
): ProgressState => {
  const defaults = createDefaultProgressState();
  const upgrades = asRecord(record?.upgrades);
  const preferences = asRecord(record?.preferences);
  const statistics =
    record?.schemaVersion === 0 ? undefined : asRecord(record?.statistics);
  const unlockedSkinIds = sanitizeUnlockedSkinIds(record?.unlockedSkinIds);
  const selectedSkinId =
    typeof record?.selectedSkinId === "string" &&
    unlockedSkinIds.includes(record.selectedSkinId as SkinId)
      ? (record.selectedSkinId as SkinId)
      : progressConfig.defaultSkinId;
  const selectedSoundtrackId: SelectedSoundtrackId = isSelectedSoundtrackId(
    record?.selectedSoundtrackId,
  )
    ? record.selectedSoundtrackId
    : defaults.selectedSoundtrackId;
  const selectedArenaBackgroundId: ArenaBackgroundId = isArenaBackgroundId(
    record?.selectedArenaBackgroundId,
  )
    ? record.selectedArenaBackgroundId
    : defaults.selectedArenaBackgroundId;
  const settledRoundReceipts = sanitizeSettlementReceipts(
    record?.settledRoundReceipts,
    new Set(),
    progressConfig.maximumSettledRoundReceipts,
  );
  const settledIdentifiers = new Set(
    settledRoundReceipts.map((receipt) => receipt.id),
  );

  return {
    dailyChallengeRecords: sanitizeDailyChallengeRecords(
      record?.dailyChallengeRecords,
    ),
    firstRunHintDismissed: sanitizeBoolean(
      record?.firstRunHintDismissed,
      defaults.firstRunHintDismissed,
    ),
    pendingRoundSettlementReceipts: sanitizeSettlementReceipts(
      record?.pendingRoundSettlementReceipts,
      settledIdentifiers,
    ),
    personalBestLevels: sanitizePersonalBestLevels(
      record,
      settledRoundReceipts,
    ),
    preferences: {
      hapticsEnabled: sanitizeBoolean(
        preferences?.hapticsEnabled,
        defaults.preferences.hapticsEnabled,
      ),
      musicEnabled: sanitizeBoolean(
        preferences?.musicEnabled,
        defaults.preferences.musicEnabled,
      ),
      reducedMotion: sanitizeBoolean(
        preferences?.reducedMotion,
        defaults.preferences.reducedMotion,
      ),
      soundEnabled: sanitizeBoolean(
        preferences?.soundEnabled,
        defaults.preferences.soundEnabled,
      ),
    },
    schemaVersion: progressConfig.schemaVersion,
    selectedArenaBackgroundId,
    selectedSkinId,
    selectedSoundtrackId,
    settledRoundReceipts,
    statistics: {
      foodCollected: sanitizeCounter(
        statistics?.foodCollected,
        defaults.statistics.foodCollected,
      ),
      roundsPlayed: sanitizeCounter(
        statistics?.roundsPlayed,
        defaults.statistics.roundsPlayed,
      ),
      snakesDefeated: sanitizeCounter(
        statistics?.snakesDefeated,
        defaults.statistics.snakesDefeated,
      ),
    },
    totalCoins: sanitizeCounter(record?.totalCoins, defaults.totalCoins),
    unlockedSkinIds,
    upgrades: {
      "food-value": sanitizeUpgrade(upgrades?.["food-value"], "food-value"),
      "movement-speed": sanitizeUpgrade(
        upgrades?.["movement-speed"],
        "movement-speed",
      ),
      "starting-size": sanitizeUpgrade(
        upgrades?.["starting-size"],
        "starting-size",
      ),
    },
  };
};

const hasFutureSchemaVersion = (
  record: Record<string, unknown> | undefined,
): boolean =>
  typeof record?.schemaVersion === "number" &&
  Number.isSafeInteger(record.schemaVersion) &&
  record.schemaVersion > progressConfig.schemaVersion;

export const serializeProgress = (state: ProgressState): string =>
  JSON.stringify(state);

export const sanitizeProgress = (value: unknown): ProgressState =>
  sanitizeProgressRecord(asRecord(value));

export const decodeProgress = (raw: string | null): ProgressDecodeResult => {
  if (raw === null) {
    return {
      needsRepair: true,
      state: createDefaultProgressState(),
      writeLocked: false,
    };
  }

  let value: unknown;
  try {
    value = JSON.parse(raw) as unknown;
  } catch {
    return {
      needsRepair: true,
      state: createDefaultProgressState(),
      writeLocked: false,
    };
  }

  const record = asRecord(value);
  if (hasFutureSchemaVersion(record)) {
    return {
      needsRepair: false,
      state: createDefaultProgressState(),
      writeLocked: true,
    };
  }

  const state = sanitizeProgressRecord(record);
  return {
    needsRepair: raw !== serializeProgress(state),
    state,
    writeLocked: false,
  };
};
